import { ReactNode, useState } from "react";
import classNames from "./FrequentlyAskedQuestions.module.css";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import { Link } from "react-router-dom";

function Question(props: { question: ReactNode; answer: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <button onClick={() => setIsOpen((prev) => !prev)}>
      <div className={classNames.QuestionToggle}>
        {props.question}
        {!isOpen ? <IoChevronDown /> : <IoChevronUp />}
      </div>
      {isOpen && <div className={classNames.Answer}>{props.answer}</div>}
    </button>
  );
}

const FrequentlyAskedQuestions = (props: { landing?: boolean }) => {
  return (
    <div className={classNames.FrequentlyAskedQuestions}>
      <div className={classNames.Titles}>
        {props.landing ? (
          <>
            <h3>FAQ</h3>
            <p>
              Explore these helpful topics to find answers to your questions
            </p>
          </>
        ) : (
          <>
            <h2>FAQ</h2>
            <h2>
              Explore these helpful topics to find answers to your questions
            </h2>
          </>
        )}
      </div>
      <div
        className={`${classNames.Questions} ${
          !props.landing ? classNames.SupportQuestions : ""
        }`}
      >
        <Question
          question="What is Langfinity, and how does it work?"
          answer={
            <p>
              Langfinity is your 24/7 AI-powered live translator for meetings
              and events in 50+ languages. With one click or a QR scan,
              individuals and teams can instantly communicate across 50+
              different languages—whether remote, hybrid, or in-person.
              Langfinity integrates seamlessly with Microsoft Teams, Google
              Meet, Zoom, and more.{" "}
              <Link target="_blank" to="/">
                Try it now
              </Link>{" "}
              to see it in action!
            </p>
          }
        />
        <Question
          question="Can Langfinity translate multiple languages at the same time? (e.g., some attendees speak Spanish, others French)"
          answer={
            <>
              <p>
                Yes! Langfinity offers real-time, multi-way translation. Unlike
                one-way translation, where translation flow in a single
                direction, Langfinity allows attendees to speak and listen in
                their preferred language at the same time. Each participant
                selects their language, and our AI instantly translates speech
                and captions—whether some speak Spanish, others French,
                Mandarin, or any of the 50+ supported languages.
              </p>
              <p>
                Langfinity ensures smooth, natural conversations in meetings,
                events, and discussions, no matter how many languages are
                spoken.
              </p>
            </>
          }
        />
        <Question
          question="What types of meetings and events can Langfinity be used for?"
          answer={
            <>
              <p>
                Langfinity is designed for any multilingual meeting or event,
                whether in-person, hybrid, or virtual.
              </p>
              <p>It's ideal for:</p>
              <p>
                • Business meetings & team collaborations - Global teams can
                communicate seamlessly.
              </p>
              <p>
                • Webinars & conferences - Attendees engage in real-time across
                languages.
              </p>
              <p>
                • Training & workshops - Employees learn in their native
                language.
              </p>
              <p>
                • Town halls & corporate events - Ensure clear communication
                across diverse teams.
              </p>
              <p>
                • Religious services & sermons - Churches and faith-based
                organizations can reach multilingual congregations.
              </p>
              <p>
                • Healthcare & telemedicine - Doctors, patients, and medical
                staff can overcome language barriers.
              </p>
              <p>
                • Customer support & contact centers - Provide real-time
                multilingual assistance.
              </p>
              <p>
                Wherever real-time, multilingual communication is needed,
                Langfinity makes it seamless.
              </p>
            </>
          }
        />

        <Question
          question="What makes Langfinity the best AI-powered real-time translation for meetings and events?"
          answer={
            <>
              <p>
                🎙 Real-time voice-to-voice translation - Hear translations in
                natural, human-like voices that match tone and personality.
              </p>
              <p>
                🗣 Multi-way translation - Attendees can speak and listen in
                different languages at the same time.
              </p>
              <p>
                🎭 Custom voice avatars - Choose from lifelike AI voices that
                preserve personality and expression.
              </p>
              <p>
                🌍 Live translation anywhere, anytime - One platform for
                seamless multilingual communication across meetings, events, and
                conferences.
              </p>
              <p>
                🔌 Seamless integration - Works with Microsoft Teams, Zoom,
                Google Meet, and more, with no special hardware needed.
              </p>
              <p>
                🔒 Highly secure & privacy-focused - Enterprise-grade encryption
                ensures full confidentiality for every conversation.
              </p>
              <p>
                ✅ Easy to use - Join with one click or a QR scan—no downloads,
                no setup hassle.
              </p>
            </>
          }
        />

        <Question
          question="What are Langfinity's key features?"
          answer={
            <>
              <p>
                🎙 Real-time voice-to-voice translation - Instantly translates
                speech into natural, human-like AI voices that preserve tone and
                personality.
              </p>
              <p>
                🗣 Multi-way translation - Attendees can speak and listen in
                different languages at the same time, enabling seamless
                multilingual conversations.
              </p>
              <p>
                🎭 Custom AI voice avatars - Choose from lifelike voice options
                that best represent personality, expression, and tonality.
              </p>
              <p>
                🌍 Live translation anywhere, anytime - A single platform for
                real-time multilingual communication in meetings, events,
                conferences, and webinars.
              </p>
              <p>
                🔌 Seamless integration - Works with Microsoft Teams, Zoom,
                Google Meet, and more without requiring special hardware.
              </p>
              <p>
                🔒 Enterprise-grade security & privacy - Fully encrypted to
                ensure confidential and secure communication.
              </p>
              <p>
                📜 Industry-specific accuracy - Glossary for terminologies,
                proper nouns, and provide domain-focused deep fine-tuning to
                enhance accuracy for industry relevant conversations.
              </p>
              <p>
                ✅ Easy to use - Join with one click or a QR scan, no downloads
                or complicated setup required.
              </p>
            </>
          }
        />

        <Question
          question="Does Langfinity support both voice-to-voice and voice-to-text translation?"
          answer={<p>Yes, we support both!</p>}
        />

        <Question
          question="How do I use Langfinity for live translation in Microsoft Teams?"
          answer={
            <>
              <p>1️⃣ Schedule a meeting - Create a Teams meeting as usual.</p>
              <p>
                2️⃣ Add Langfinity - Click the meeting in your calendar, select
                "+"" (Apps), search for "Byrdhouse AI" or "Langfinity", and add
                our Teams app to your meeting.
              </p>
              <p>
                3️⃣ Sign in - Use Single Sign-On (SSO) with your Microsoft
                account.
              </p>
              <p>4️⃣ Invite AI interpreter bot.</p>
              <p>5️⃣ Choose your language - Select the language you speak.</p>
              <p>
                6️⃣ Translation enabled automatically. Click on "Voice" to enable
                voice translation
              </p>
              <p>7️⃣ Customize text size, speed, and voice avatars.</p>
              <p>
                8️⃣ End translation - Translation stops when the meeting ends or
                if you remove Langfinity from Teams.
              </p>
              <p>
                That's it! Communicate effortlessly in multiple languages. For
                more details see onboarding guide{" "}
                <Link
                  target="_blank"
                  to="https://langfinity.notion.site/Langfinity-for-Microsoft-Teams-1b991ab1cc7f80698442d47c713bcf24?pvs=4"
                >
                  here
                </Link>
                .
              </p>
            </>
          }
        />

        <Question
          question="How many languages does Langfinity support?"
          answer={
            <p>
              We support 50+ languages. To learn what they are,{" "}
              <Link target="_blank" to="/">
                sign up here
              </Link>{" "}
              and view in your homepage!
            </p>
          }
        />

        <Question
          question="How accurate is the translation?"
          answer={
            <p>
              {
                "All baseline models for popular languages achieve >85% translation accuracy, with even greater accuracy through industry-specific fine-tuning."
              }
            </p>
          }
        />

        <Question
          question="Can I try Langfinity before purchasing to test translation accuracy and speed?"
          answer={
            <p>
              Yes! You can try Langfinity for 20 minutes for free to test
              translation accuracy and speed.{" "}
              <Link target="_blank" to="/">
                Simply sign up
              </Link>{" "}
              for a trial, join a meeting, and experience real-time voice and
              text translation in action. No credit card or setup required.
            </p>
          }
        />

        <Question
          question="How many participants can Langfinity support in a single event?"
          answer={<p>Up to 100K participants.</p>}
        />

        <Question
          question="Does Langfinity require an internet connection, or can it work offline?"
          answer={<p>Yes, it requires an internet connection.</p>}
        />

        <Question
          question="What setup is required for Langfinity to translate fast and accurate?"
          answer={
            <>
              <p>
                For Langfinity to provide fast and accurate real-time
                translation, users need to ensure the following setup:
              </p>
              <p>
                🎤 Microphone access: Users must grant Langfinity access to
                their microphones to capture spoken language in real-time. A
                high-quality microphone or headset is preferred.
              </p>
              <p>
                🌐 Stable internet connection: A strong and reliable internet
                connection is essential for seamless communication and data
                transmission.
              </p>
              <p>
                🖥️ Supported browser: Chrome is preferred for the best
                performance and compatibility with Langfinity's real-time
                features.
              </p>
              <p>
                🔊 Optimal audio conditions: Speaking clearly in a quiet
                environment helps reduce background noise, improving speech
                recognition accuracy.
              </p>
            </>
          }
        />

        <Question
          question="Can Langfinity integrate with our existing sound system and microphones?"
          answer={
            <p>
              Yes, as long as your existing sound system can be connected to a
              device that can access Langfinity, you can use it. The easiest way
              is to connect via USB to a laptop and ensure the device is
              selected in audio settings in the room.
            </p>
          }
        />

        <Question
          question="Can attendees listen to translations on their smartphones, or do they need separate headsets?"
          answer={
            <p>
              Yes! Attendees can listen on their smartphones by joining via a
              link or QR code. We still reccomend listening with headphones
              connected to your smartphone during events with larger crowds to
              avoid disrupting the other attendees listening in different
              languages.
            </p>
          }
        />

        <Question
          question="Can speakers talk at their natural pace, or do they need to slow down for better accuracy?"
          answer={
            <p>
              Speakers can use their natural tone and pace during voice-to-voice
              translation. Langfinity offers a variety of voice avatars to match
              different personalities and speaking styles. For the best
              experience, we recommend speaking clearly and at a steady pace,
              allowing the AI to capture key details so listeners can fully
              understand and engage with the content.
            </p>
          }
        />

        <Question
          question="Can multiple human interpretesrs be used at the same time for different languages?"
          answer={
            <p>
              Yes! Langfinity supports multiple human interpreters alongside AI
              translation. You can assign different interpreters for various
              languages in the same session, ensuring accurate and natural
              translations for all attendees. See more details on how to use
              human interpreter channel{" "}
              <Link
                target="_blank"
                to="https://langfinity.notion.site/Langfinity-for-events-or-conferences-1b991ab1cc7f80fda9fed6339aceb6e9?pvs=4"
              >
                here
              </Link>
              .
            </p>
          }
        />

        <Question
          question="Can users adjust the volume and speed of translations on their own devices?"
          answer={
            <p>
              Yes! Users can adjust the volume and speed of translations on
              their own devices. They can personalize their listening experience
              by increasing or decreasing the volume, customizing the text color
              or size, and adjusting the translation speed for better
              understanding and comfort.
            </p>
          }
        />

        <Question
          question="Does Langfinity support multilingual recordings and transcripts? Can translations be saved for later reference?"
          answer={
            <p>
              Yes! Langfinity supports multilingual recordings and transcripts.
              All translated speech and text can be saved and accessed later,
              allowing users to review conversations, meetings, and events in
              multiple languages.
            </p>
          }
        />

        <Question
          question="How much does Langfinity cost?"
          answer={
            <>
              <p>
                Langfinity offers flexible pricing based on your needs. We
                provide different plans for businesses, events, and
                organizations, with options based on translation hours, number
                of languages, and features like voice-to-voice translation and
                AI voice avatars.
              </p>
              <p>
                📩 Contact us at team@langfinity.ai for a custom quote or a free
                trial!
              </p>
            </>
          }
        />

        <Question
          question="What security and compliance measures does Langfinity follow? How secure is my meeting and event data?"
          answer={
            <p>
              Langfinity follows best practices to ensure the security of your
              meeting and event data. This includes encrypting data both at rest
              and in transit, implementing strict access controls, and
              conducting regular security audits. The platform uses secure
              infrastructure with firewalls and intrusion detection systems to
              protect against threats, while also adhering to data retention and
              disposal policies to ensure data is kept only as long as necessary
              and securely deleted when no longer needed. These measures work
              together to maintain the confidentiality and integrity of your
              data.
            </p>
          }
        />

        <Question
          question="Is Langfinity easy to set up and use for non-technical users?"
          answer={
            <>
              <p>
                Yes! Langfinity is designed to be easy for everyone, even
                non-technical users.
              </p>
              <p>
                ✅ One-click access - Join with a simple link or QR scan, no
                complicated setup.
              </p>
              <p>
                ✅ Seamless integration - Works with Microsoft Teams, Zoom,
                Google Meet, and more.
              </p>
              <p>
                ✅ Intuitive interface - Just select your language and start
                translating in real time.
              </p>
              <p>
                No training or technical skills needed—just start speaking!
                Check out the onboarding guide{" "}
                <Link
                  target="_blank"
                  to="https://langfinity.notion.site/Langfinity-onboarding-guide-1b991ab1cc7f803aa776f7be6b60ac67?pvs=4"
                >
                  here
                </Link>{" "}
                for details.
              </p>
            </>
          }
        />

        <Question
          question="What are the best setup recommendations for real-time translation at events?"
          answer={
            <p>
              For events we recommend connecting the existing sound system
              directly to Langfinity to acheive the best sound quality. You can
              do this by connecting a laptop to your event sound system via USB
              or Aux. This will ensure the most accurate translations for the
              speaker since it will use the same source the audience will hear.
              A second-best alternative is to attach a wireless lapel mic to the
              presenter and connect it to a laptop with Langfinity.
            </p>
          }
        />
      </div>
    </div>
  );
};

export default FrequentlyAskedQuestions;
